import React from "react";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";

import { Button } from "../../../../theme";
import CovidLethalityMapController from "./CovidLethalityMapController";

import evolutionPlot57 from "./assets/evolution_57.svg";
import evolutionPlot60 from "./assets/evolution_60.svg";
import evolutionPlot67 from "./assets/evolution_67.svg";
import evolutionPlot68 from "./assets/evolution_68.svg";
import evolutionPlot75 from "./assets/evolution_75.svg";
import evolutionPlot88 from "./assets/evolution_88.svg";
import evolutionPlot92 from "./assets/evolution_92.svg";
import evolutionPlot93 from "./assets/evolution_93.svg";
import evolutionPlot94 from "./assets/evolution_94.svg";
import evolutionPlot95 from "./assets/evolution_95.svg";

import comparePlot57 from "./assets/compare_57.svg";
import comparePlot60 from "./assets/compare_60.svg";
import comparePlot67 from "./assets/compare_67.svg";
import comparePlot68 from "./assets/compare_68.svg";
import comparePlot75 from "./assets/compare_75.svg";
import comparePlot88 from "./assets/compare_88.svg";
import comparePlot92 from "./assets/compare_92.svg";
import comparePlot93 from "./assets/compare_93.svg";
import comparePlot94 from "./assets/compare_94.svg";
import comparePlot95 from "./assets/compare_95.svg";

import repartitionPlot from "./assets/repartition.svg";

import francePlot from "./assets/france.svg";

function CovidLethality() {
  return (
    <>
      <p>
        Alors que{" "}
        <a
          href="https://www.lemonde.fr/international/article/2020/03/30/coronavirus-doutes-sur-l-estimation-du-nombre-de-deces-en-chine_6034871_3210.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          des estimations récentes suggèrent
        </a>{" "}
        que le décompte des victimes aurait été largement sous-évalué en Chine,
        mais aussi en{" "}
        <a
          href="https://www.economist.com/graphic-detail/2020/04/03/covid-19s-death-toll-appears-higher-than-official-figures-suggest"
          target="_blank"
          rel="noopener noreferrer"
        >
          Italie et en Espagne
        </a>
        , l’Insee a décidé de publier exceptionnellement chaque semaine les
        chiffres de mortalité par département. Des données qui semblent indiquer
        qu’en France également, le Covid-19 tue beaucoup plus qu’annoncé.
      </p>

      <p>
        Conscient des limites des statistiques officielles qui se cantonnent aux
        décès survenus en milieu hospitalier,{" "}
        <a
          href="https://www.lemonde.fr/societe/article/2020/03/20/coronavirus-le-ministere-de-l-interieur-demande-aux-prefets-de-tenter-de-recenser-les-morts_6033787_3224.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          le gouvernement avait demandé aux préfets
        </a>
        , le 19 mars, de recenser les morts afin de comparer la mortalité entre
        2019 et 2020 sur la période du 1er mars au 18 mars, sans dévoiler les
        données ainsi collectées. Mais l’Insee lui a emboîté le pas et a décidé
        de publier plus régulièrement, dès vendredi 27 mars, les nombres de
        décès transmis par toutes les communes de France. Grâce à ces données,{" "}
        <em>Le Média</em> a pu réaliser une estimation indépendante du nombre de
        victimes “invisibles” du nouveau coronavirus dans plusieurs
        départements.
      </p>

      <h2>Une explosion de la mortalité en mars 2020</h2>

      <p>
        Pour cela, nous avons d’abord comparé l’évolution du nombre de décès
        cumulés du 1er mars au 30 mars, pour les années 2019 et 2020. Voilà ce
        que l’on obtient dans le département du Haut-Rhin, département le plus
        touché jusqu’à présent :
      </p>

      <figure>
        <img
          src={evolutionPlot68}
          alt="Les courbes de mortalité cumulée en 2019 et 2020 se confondent
          jusqu'aux alentours du 10 mars.
          Après cette date, alors que la courbe de 2019 poursuit une croissance linéaire
          pour atteindre un peu moins de 600 décès au 30 mars,
          la courbe de 2020 augmente très rapidement
          et dépasse les 1.200 décès au 30 mars."
        />
        <figcaption>
          <strong>
            Mortalité cumulée en mars 2019 et mars 2020 (Haut-Rhin).
          </strong>
        </figcaption>
      </figure>

      <p>
        Alors que la mortalité suit la même tendance en 2019 et en 2020 jusqu’au
        10 mars environ, elle explose cette année à partir de cette date. Au
        final, la mortalité a plus que doublé au mois de mars dans le Haut-Rhin.
        Un phénomène que l’on observe dans plusieurs départements du Grand-Est
        et de l’Ile de France, les principaux foyers de l’épidémie, avec des
        dates de décrochage légèrement différentes :
      </p>

      <figure className="flux-image-half">
        <img src={evolutionPlot57} alt="Moselle" />
        <img src={evolutionPlot67} alt="Bas-Rhin" />
        <img src={evolutionPlot88} alt="Vosges" />
        <img src={evolutionPlot60} alt="Oise" />
        <img src={evolutionPlot75} alt="Paris" />
        <img src={evolutionPlot92} alt="Hauts-de-Seine" />
        <img src={evolutionPlot93} alt="Seine-Saint-Denis" />
        <img src={evolutionPlot94} alt="Val-de-Marne" />
        <img src={evolutionPlot95} alt="Val d'Oise" />
        <figcaption>
          <strong>
            Mortalité cumulée en mars 2019 et mars 2020 dans divers
            départements.
          </strong>
        </figcaption>
      </figure>

      <p>
        On observe également, dans les départements étudiés, que le moment où la
        mortalité en 2020 commence à s’écarter de la mortalité relevée en 2019
        correspond au moment où l’épidémie a débuté. L’exemple du département de
        l’Oise, premier “cluster” français, est à ce titre particulièrement
        éloquent.
      </p>

      <h2>
        Une surmortalité deux à trois fois supérieure au nombre officiel de
        victimes dans les départements les plus touchés
      </h2>

      <p>
        On estime la surmortalité pour chaque département en calculant la
        différence entre le nombre de décès relevés au mois de mars 2020 et le
        nombre de décès relevé en mars 2019. Une telle approche est justifiée
        par la similarité du taux de mortalité entre ces deux années avant que
        l’épidémie ne se concrétise. La surmortalité ainsi calculée peut être
        interprétée comme le nombre de morts inhabituelles. Cette surmortalité
        peut ensuite être comparée au nombre officiel de victimes du Covid-19.
        Les résultats pour 11 départements parmi les plus touchés sont résumés
        dans le tableau suivant :
      </p>

      <div className="flux-table-responsive-wrapper">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell component="th" scope="col">
                Département
              </TableCell>
              <TableCell component="th" scope="col">
                Surmortalité en&nbsp;mars (2020&nbsp;-&nbsp;2019)
              </TableCell>
              <TableCell component="th" scope="col">
                Victimes officielles du Covid-19
              </TableCell>
              <TableCell component="th" scope="col">
                Rapport surmortalité / chiffres officiels
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row">
                Haut-Rhin
              </TableCell>
              <TableCell>730 ± 20 (+128 %)</TableCell>
              <TableCell>380</TableCell>
              <TableCell>1,9</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Paris
              </TableCell>
              <TableCell>520 ± 40 (+35 %)</TableCell>
              <TableCell>306</TableCell>
              <TableCell>1,7</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Hauts-de-Seine
              </TableCell>
              <TableCell>460 ± 30 (+55 %)</TableCell>
              <TableCell>127</TableCell>
              <TableCell>3,6</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Seine-Saint-Denis
              </TableCell>
              <TableCell>400 ± 30 (+61 %)</TableCell>
              <TableCell>124</TableCell>
              <TableCell>3,2</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Moselle
              </TableCell>
              <TableCell>360 ± 30 (+40 %)</TableCell>
              <TableCell>182</TableCell>
              <TableCell>2,0</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Bas-Rhin
              </TableCell>
              <TableCell>340 ± 30 (+37 %)</TableCell>
              <TableCell>153</TableCell>
              <TableCell>2,2</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Val-de-Marne
              </TableCell>
              <TableCell>310 ± 30 (+38 %)</TableCell>
              <TableCell>102</TableCell>
              <TableCell>3,0</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Val d'Oise
              </TableCell>
              <TableCell>250 ± 30 (+38 %)</TableCell>
              <TableCell>102</TableCell>
              <TableCell>3,0</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Yvelines
              </TableCell>
              <TableCell>230 ± 30 (+31 %)</TableCell>
              <TableCell>80</TableCell>
              <TableCell>2,8</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Oise
              </TableCell>
              <TableCell>220 ± 20 (+41 %)</TableCell>
              <TableCell>90</TableCell>
              <TableCell>2,2</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Vosges
              </TableCell>
              <TableCell>190 ± 20 (+55 %)</TableCell>
              <TableCell>81</TableCell>
              <TableCell>2,4</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <p>
        Dans ces départements, on observe globalement une surmortalité deux à
        trois fois plus importante que le nombre officiel de victimes du
        Covid-19. Cette différence pourrait avoir plusieurs explications. Par
        exemple, les décès survenus dans les EHPAD seraient mal comptabilisés.
        D’autres victimes, décédées chez elles, échapperaient également aux
        statistiques. C’est en tout cas ce que semble confirmer un témoignage
        que nous avons recueilli auprès d’un dirigeant d’une société de pompes
        funèbres basée dans les Hauts-de-Seine, et qui constate une nette hausse
        de la mortalité : «{" "}
        <em>
          J’ai des cas qui sont traités déjà, il est clair que c’est du covid
          19, mais ils ne sont pas déclarés. On les prend en charge dans des
          EHPAD, dans les domiciles. Samedi dernier par exemple, on est allé
          chercher quelqu’un dans un EHPAD. La famille me prévient que c’est un
          Covid-19, car ils ont effectué un test. Mais ça ne figurait pas sur le
          certificat de décès.
        </em>{" "}
        ». Par ailleurs, la surmortalité comprend les victimes indirectes, comme
        des personnes non atteintes par le virus mais décédées faute de prise en
        charge adéquate en raison de la saturation du système de santé.
      </p>

      <p>
        Paris fait figure d’exception d’après ces chiffres préliminaires, car la
        surmortalité semble en accord avec les chiffres officiels. Mais la
        mortalité a pu y être diminuée par le confinement et l’
        <a
          href="https://www.francetvinfo.fr/sante/maladie/coronavirus/confinement-un-paris-vide-de-ses-habitants-soffre-aux-photographes_3892115.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          exode massif qu’il a engendré depuis la capitale
        </a>
        .
      </p>

      <p>
        La surmortalité que nous constatons pour ce qui concerne le mois de mars
        2020 qui vient de s’écouler est-elle réellement attribuable au Covid-19
        ? Pour le vérifier, nous pouvons confronter la carte de la surmortalité
        et des décès Covid-19 officiels. La corrélation est flagrante :
      </p>

      <CovidLethalityMapController version="v1" />

      <p>
        Un autre moyen de vérifier que la surmortalité est bien attribuable au
        Covid-19 est de comparer son évolution avec l’évolution du nombre
        officiel de victimes. Là encore, la corrélation est flagrante, notamment
        dans le Haut-Rhin :
      </p>

      <figure>
        <img
          src={comparePlot68}
          alt="Jusqu'au 10 mars, la surmortalité cumulée est nulle et
          aucun décès Covid-19 n'est déclaré.
          À partir de cette date, la surmortalité cumulée augmente
          à un rythme constant.
          Le nombre officiel de victimes du Covid-19 augmente simultanément,
          mais à un rythme deux fois inférieur environ."
        />
        <figcaption>
          <strong>
            Surmortalité et nombre officiel de victimes du Covid-19 cumulés
            (Haut-Rhin)
          </strong>
        </figcaption>
      </figure>

      <figure className="flux-image-half">
        <img src={comparePlot57} alt="Moselle" />
        <img src={comparePlot67} alt="Bas-Rhin" />
        <img src={comparePlot88} alt="Vosges" />
        <img src={comparePlot60} alt="Oise" />
        <img src={comparePlot75} alt="Paris" />
        <img src={comparePlot92} alt="Hauts-de-Seine" />
        <img src={comparePlot93} alt="Seine-Saint-Denis" />
        <img src={comparePlot94} alt="Val-de-Marne" />
        <img src={comparePlot95} alt="Val d'Oise" />
        <figcaption>
          <strong>
            Surmortalité et nombre officiel de victimes du Covid-19 cumulés dans
            divers départements.
          </strong>
        </figcaption>
      </figure>

      <p>
        Vendredi 3 avril 2020, le directeur général de la santé, Jérôme Salomon,
        a annoncé 1.416 morts dans les EHPAD depuis le début de l’épidémie. Ils
        viennent s’ajouter aux 4.500 victimes recensées jusqu’à cette date dans
        les hôpitaux, pour un total d'environ 6.000 morts. Cela ne semble pas
        suffire à combler l'écart avec la surmortalité que nous constatons, deux
        fois supérieure aux chiffres officiels des décès liés au coronavirus
        pour le mois de mars dans les départements les plus touchés.
      </p>

      <p>
        Pour faire la lumière sur ces discordances, chaque semaine,{" "}
        <em>Le Média</em> mettra à jour son analyse sur cette page avec les
        données publiées par l’Insee.
      </p>

      <p>
        <strong>Mise à jour du 07/04/2020</strong>
        <br />
        Vous avez été nombreux à lire et partager cette analyse de la
        surmortalité dans le cadre du Covid-19. Nous avons donc décidé de
        publier, pour les curieux, un complément sur notre démarche.
      </p>
      <p className="text-center" style={{ marginBottom: "4.8rem" }}>
        <Button
          as="a"
          href="https://lemediatv.fr/articles/2020/statique/les-morts-invisibles-du-coronavirus-making-of-SaYKcOT9RFaTkUMNO7HOYg"
        >
          Lire le complément
        </Button>
      </p>

      <p>
        <strong>Mise à jour du 10/04/2020</strong>
        <br />
        Au 9 avril, les chiffres officiels indiquent désormais 8 044 décès
        survenus dans les hôpitaux et 4 166 décès dans les EHPAD et
        établissements médico-sociaux. Mais d'après nos estimations, la
        surmortalité est 2 à 3 fois supérieure au nombre de décès dus au
        Covid-19 survenant dans les hôpitaux. Les données officielles remontées
        des EHPAD ne semblent toujours pas combler l'écart à ce stade. Comment
        l'expliquer ?
      </p>
      <p>
        Grâce à des données publiées aujourd'hui par l'Insee, nous pouvons
        envisager quelques pistes. L'institut a en effet mis en ligne des
        données de mortalité classées par lieu de décès parmi plusieurs
        catégories : les décès survenus dans les hôpitaux et cliniques, en
        maison de retraite, ou à domicile. Nous pouvons ainsi estimer la
        surmortalité pour chacune de ces catégories. Voici le résultat pour les
        départements les plus touchés :
      </p>

      <figure>
        <img
          src={repartitionPlot}
          alt="Dans les trois départements les plus touchés
          (le Haut-Rhin, Paris, et les Hauts-de-Seine),
          la moitié de la surmortalité environ provient des hôpitaux et cliniques.
          Le reste se partage essentiellement entre les décès survenus à domicile,
          et dans les maisons de retraite ensuite.
          La composante de la surmortalité issue à domicile
          semble comparable à celle survenue dans les EHPAD."
        />
        <figcaption>
          <strong>
            Variation de la mortalité entre mars 2019 et mars 2020, par lieu de
            décès.
          </strong>{" "}
          (Source: Insee, 10/04/2020)
        </figcaption>
      </figure>

      <p>
        Dans le Haut-Rhin, à Paris, et dans les Hauts-de-Seine, les décès
        survenus à l'hôpital n'expliquent qu'environ la moitié de la
        surmortalité, ce qui conforte nos observations précédentes. Le reste se
        partage à peu près équitablement entre les décès survenus en maison de
        retraite et à domicile. Or, à ce stade, les autorités sanitaires ne
        signalent pas les décès survenus à domicile et imputables au Covid-19.
        Si les décès survenus à domicile sont certainement plus fréquents en
        raison du confinement, il semble qu'ils aient surtout augmenté dans les
        départements les plus touchés par la maladie, ce qui suggère qu'une
        partie significative des victimes "invisibles" du Covid-19 échapperait
        aux statistiques en mourrant chez elles.
      </p>

      <p>
        Pour connaître la répartition détaillée de la surmortalité par lieu de
        décès par zone géographique, survolez les départements sur la carte de
        gauche :
      </p>

      <CovidLethalityMapController version="v2" />

      <p>
        <strong>Mise à jour du 26/04/2020</strong>
        <br />
        Les chiffres de mortalité publiés le 24/04/2020 permettent une
        comparaison approximative de la surmortalité par rapport à 2019 et du
        nombre de décès officiellement attribués au Covid-19. Cette fois, on
        évalue également la surmotalité par rapport à la mortalité moyenne entre
        2010 et 2019, à titre de comparaison.
      </p>

      <figure>
        <img
          src={francePlot}
          alt="La surmortalité cumulée depuis le 1er mars,
            qu'elle soit évaluée par rapport à 2019 ou à la moyenne de 2010 à 2O19,
            approche les 19.000 au 13 avril 2020,
            pour un peu moins de 15.000 décès attribués au Covid-19."
        />
        <figcaption>
          <strong>
            Surmortalité cumulée depuis le 1er mars 2020 en France, et décès
            officiellements attribués au Covid-19.
          </strong>
          <br />
          La surmortalité est évaluée de deux façons : par rapport à la
          mortalité observée en 2019 (en rouge), et par rapport à la moyenne
          observée entre 2010 et 2019 (en violet). Elle est comparée au nombre
          de décès officiellement attribués au Covid-19 (Hôpital + EHPAD, points
          noirs). <em>(Source : Insee, 24/04/2020)</em>
        </figcaption>
      </figure>

      <p>
        Les deux estimations de la surmortalité approchent les 19.000 décès
        cumulés entre le 1er mars et le 13 avril, à comparer aux 14.967 décès
        attribués au Covid-19 à cette date, EHPAD inclus. Le sous-comptage
        probable des décès dus au Covid-19 est donc désormais visible au niveau
        national également.
      </p>

      <p>
        <em>
          Sources : Insee, Agences Régionales de Santé, Santé Publique France.
        </em>
      </p>
    </>
  );
}

export default CovidLethality;
